import {
  ChangeDetectionStrategy,
  Component,
  input,
  inject,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AnyApp,
  AppEntry,
  PersonalApp,
} from '../../../core/models/app-entry.model';
import { AuthService } from '../../../api/services/auth.service';
import { WorkerService } from '../../../api/services/worker.service';

@Component({
  selector: 'ec-rail-item',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (opensNewWindow()) {
      <button
        class="rail-item"
        [attr.aria-label]="app().label"
        (click)="launchExternal()"
      >
        <span class="rail-item__accent" aria-hidden="true"></span>
        <img
          [src]="iconSrc()"
          [alt]="app().label"
          class="rail-item__icon"
          width="40"
          height="40"
        />
        <span class="rail-item__label">{{ app().label }}</span>
      </button>
    } @else {
      <a
        class="rail-item"
        [class.rail-item--active]="isActive"
        [attr.aria-label]="app().label"
        [routerLink]="appRoute()"
        (click)="handleNavigate($event)"
      >
        <span class="rail-item__accent" aria-hidden="true"></span>
        <img
          [src]="iconSrc()"
          [alt]="app().label"
          class="rail-item__icon"
          width="40"
          height="40"
        />
        <span class="rail-item__label">{{ app().label }}</span>
      </a>
    }
  `,
  styles: [
    `
      .rail-item {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        width: 100%;
        padding: 10px 6px;
        cursor: pointer;
        background: transparent;
        border: none;
        text-decoration: none;
        transition: background var(--transition-fast);

        &:hover {
          background-color: #141c40;
        }
        &:hover .rail-item__icon {
          opacity: var(--rail-icon-opacity-hover);
        }
        &:hover .rail-item__label {
          opacity: 0.9;
        }
      }

      .rail-item--active {
        background-color: #141c40;
      }
      .rail-item--active .rail-item__icon {
        opacity: var(--rail-icon-opacity-active);
      }
      .rail-item--active .rail-item__accent {
        opacity: 1;
      }
      .rail-item--active .rail-item__label {
        opacity: 1;
        color: #fff;
      }

      .rail-item__accent {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: var(--rail-accent-bar);
        border-radius: 0 2px 2px 0;
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .rail-item__icon {
        display: block;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        transition: opacity var(--transition-fast);
      }

      .rail-item__label {
        display: block;
        font-size: 11px;
        font-weight: 500;
        line-height: 1.3;
        text-align: center;
        color: rgba(255, 255, 255, 0.55);
        letter-spacing: 0.01em;
        word-break: break-word;
        hyphens: auto;
        max-width: 100%;
        transition:
          opacity var(--transition-fast),
          color var(--transition-fast);
      }

      @media (max-height: 800px) {
        .rail-item {
          padding: 7px 6px;
          gap: 4px;
        }

        .rail-item__icon {
          width: 28px;
          height: 28px;
        }

        .rail-item__label {
          font-size: 10px;
        }
      }
    `,
  ],
})
export class RailItemComponent implements OnInit, OnDestroy {
  readonly app = input.required<AnyApp>();
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly workerService = inject(WorkerService);
  private routerSub?: Subscription;

  isActive = false;

  ngOnInit(): void {
    this.checkActive();
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.checkActive();
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private checkActive(): void {
    const url = this.router.url.split('?')[0].split('#')[0];
    const a = this.app();
    let newActive = false;

    if (a.isPersonal) {
      newActive = url.startsWith(`/app/${a.id}`);
    } else if (a.id === 'ecompliance') {
      newActive =
        url.includes('/compliance-dashboard') ||
        url.includes('/framework-library') ||
        url.includes('/framework-details') ||
        url.includes('/policy-management') ||
        url.includes('/evidence-library') ||
        url.startsWith('/ecompliance');
    } else if (a.id === 'erisk') {
      newActive =
        url.includes('/risk-dashboard') ||
        url.includes('/system-risk') ||
        url.includes('/risk-register') ||
        url.includes('/manual-risk') ||
        url.includes('/risk-details') ||
        url.startsWith('/erisk');
    } else if (a.id === 'scc') {
      if (url.startsWith('/eciso')) {
        const isEcompliance =
          url.includes('/compliance-dashboard') ||
          url.includes('/framework-library') ||
          url.includes('/framework-details') ||
          url.includes('/policy-management') ||
          url.includes('/evidence-library');

        const isErisk =
          url.includes('/risk-dashboard') ||
          url.includes('/system-risk') ||
          url.includes('/risk-register') ||
          url.includes('/manual-risk') ||
          url.includes('/risk-details');

        const isIntegrations = url.includes('/integration-marketplace');

        const isNotifications =
          url.includes('/notifications') ||
          url.includes('/notification-preferences');

        const isSettings =
          url.includes('/onboarding') ||
          url.includes('/organization-setting') ||
          url.includes('/employees') ||
          url.includes('/access-management') ||
          url.includes('/my-profile') ||
          url.includes('/general-settings') ||
          url.includes('/appearance') ||
          url.includes('/branding') ||
          url.includes('/reorder') ||
          url.includes('/personal') ||
          url.includes('/settings');

        newActive =
          !isEcompliance &&
          !isErisk &&
          !isIntegrations &&
          !isNotifications &&
          !isSettings;
      } else {
        newActive = false;
      }
    } else {
      //  newActive = url.startsWith(a.route);
      const routePath = a.route.endsWith('/') ? a.route.slice(0, -1) : a.route;
      newActive = url === routePath || url.startsWith(routePath + '/');
    }

    if (this.isActive !== newActive) {
      this.isActive = newActive;
      this.cdr.markForCheck();
    }
  }

  opensNewWindow(): boolean {
    const a = this.app();
    return !!(a.isPersonal && (a as PersonalApp).openInNewWindow);
  }

  appRoute(): string {
    const a = this.app();
    return a.isPersonal ? `/app/${a.id}` : a.route;
  }

  iconSrc(): string {
    const a = this.app();
    return a.isPersonal
      ? (a as PersonalApp).iconDataUrl
      : (a as AppEntry).iconPath;
  }

  launchExternal(): void {
    const a = this.app() as PersonalApp;
    window.open(a.url, '_blank', 'noopener,noreferrer');
  }

  handleNavigate(event: MouseEvent): void {
    const a = this.app();
    const targetApps = ['dmarc', 'escr', 'edai', 'emradar', 'easm', 'evm'];
    if (targetApps.includes(a.id)) {
      event.preventDefault();
      const token = this.authService.token;
      console.log(`[RailItem] Navigating to app "${a.id}", verifying token...`);

      const doRefreshAndNavigate = () => {
        const refreshToken = localStorage.getItem('refreshToken');
        console.log(
          '[RailItem] Initiating token refresh with refreshToken:',
          refreshToken,
        );
        if (refreshToken) {
          localStorage.setItem('lastVisitedUrl', this.appRoute());

          // Subscribe to token refresh events to navigate on completion
          this.authService.tokenRefreshed$.pipe(take(1)).subscribe(() => {
            const tokenPayload = {
              accessToken: localStorage.getItem('accessToken'),
              refreshToken: localStorage.getItem('refreshToken'),
              email: localStorage.getItem('email'),
              role: localStorage.getItem('role'),
            };

            console.log(
              '[RailItem] Token refreshed successfully. Posting TOKEN_REFRESHED message to iframes:',
              tokenPayload,
            );

            const iframes = document.querySelectorAll('iframe');
            iframes.forEach((iframeEl) => {
              if (iframeEl && iframeEl.contentWindow) {
                console.log(
                  '[RailItem] Dispatching postMessage to iframe:',
                  iframeEl,
                );
                iframeEl.contentWindow.postMessage(
                  {
                    type: 'TOKEN_REFRESHED',
                    ...tokenPayload,
                  },
                  '*',
                );
              }
            });

            this.router.navigateByUrl(this.appRoute());
          });
          this.authService.tokenRefreshFailed$.pipe(take(1)).subscribe(() => {
            console.error(
              '[RailItem] Token refresh failed, navigating to unauthorized error page.',
            );
            this.router.navigate(['/error/unauthorized']);
          });

          this.workerService.refreshToken();
        } else {
          console.warn(
            '[RailItem] No refreshToken found in localStorage, redirecting to unauthorized.',
          );
          this.router.navigate(['/error/unauthorized']);
        }
      };

      this.authService.verifyToken(token).subscribe({
        next: (res: any) => {
          console.log('[RailItem] verifyToken response:', res);
          if (
            res &&
            (res.success === false ||
              res.valid === false ||
              res.status === false)
          ) {
            console.log('[RailItem] Token invalid in response, refreshing...');
            doRefreshAndNavigate();
          } else {
            console.log(
              '[RailItem] Token valid, navigating directly to app route:',
              this.appRoute(),
            );
            this.router.navigateByUrl(this.appRoute());
          }
        },
        error: (err) => {
          console.error('[RailItem] verifyToken error:', err);
          if (
            err?.status === 401 ||
            err?.status === 403 ||
            err?.status === 400 ||
            err?.status === 0
          ) {
            console.log(
              '[RailItem] Token verification error status detected, attempting refresh...',
            );
            doRefreshAndNavigate();
          } else {
            this.router.navigateByUrl(this.appRoute());
          }
        },
      });
    }
  }
}
